import React, { useState, useEffect } from "react";
import { tasksAPI, employeesAPI, contactsAPI, companiesAPI } from "../../../services/api";
import { toast } from "react-toastify";
import { ChevronDown, Search, X, Lock } from "lucide-react";

/* ─── Searchable dropdown ─── */
const SearchableDropdown = ({ value, onChange, options, placeholder, loading, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );
  const selected = options.find(o => o.value === value);

  // Locked / read-only display
  if (disabled) {
    return (
      <div className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-500 cursor-not-allowed select-none">
        <span className="truncate">{selected?.label || placeholder}</span>
        <Lock size={13} className="text-gray-400 flex-shrink-0 ml-2" />
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-left hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
      >
        {loading ? (
          <span className="text-gray-400">Loading...</span>
        ) : (
          <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
            {selected ? selected.label : placeholder}
          </span>
        )}
        <ChevronDown size={14} className={`text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && !loading && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 sticky top-0 bg-white">
            <Search size={13} className="text-gray-400 flex-shrink-0" />
            <input
              type="text" placeholder="Search..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 text-sm outline-none placeholder:text-gray-400"
              autoFocus
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
                <X size={12} />
              </button>
            )}
          </div>
          <ul style={{ maxHeight: '400px', overflowY: 'auto' }} className="py-1">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-gray-400 text-center">No results found</li>
            ) : filtered.map(opt => (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => { onChange(opt.value); setOpen(false); setSearch(''); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-blue-50 transition-colors ${
                    value === opt.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {opt.label.charAt(0).toUpperCase()}
                  </div>
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   CreateTaskModal

   Props:
     lockContact       – boolean (default false)
                         true  → linkedContact is pre-filled & locked (ContactPreviewPage)
                         false → linkedContact is a free searchable dropdown (Tasks page)
     linkedContact     – contact ID  (used when lockContact=true)
     linkedContactName – display name (used when lockContact=true)
     linkedAccount     – company ID  (pre-filled but always editable)
     linkedAccountName – display name for the pre-filled company
═══════════════════════════════════════════════════════════════ */
const CreateTaskModal = ({
  onClose,
  onCreate,
  lockContact       = false,   // ← KEY PROP
  linkedContact     = '',
  linkedContactName = '',
  linkedAccount     = '',
  linkedAccountName = '',
}) => {
  const [loading, setLoading]               = useState(false);
  const [errors, setErrors]                 = useState({});
  const [employees, setEmployees]           = useState([]);
  const [contacts, setContacts]             = useState([]);
  const [companies, setCompanies]           = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [loadingContacts, setLoadingContacts]   = useState(!lockContact); // skip fetch when locked
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  const [formData, setFormData] = useState({
    subject:       "",
    type:          "",
    dueDate:       "",
    reminder:      "NO_REMINDER",
    priority:      "",
    status:        "PENDING",
    assignedTo:    "",
    linkedContact: linkedContact,
    linkedAccount: linkedAccount,
    description:   "",
    notes:         "",
  });

  /* ── Fetch employees ── */
  useEffect(() => {
    const fetch = async () => {
      try {
        const res  = await employeesAPI.getAllEmployees();
        const data = Array.isArray(res.data) ? res.data : [];
        const mapped = data
          .map(u => ({
            value: u.id,
            label: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unknown',
            roles: u.roles || [],
          }))
          .filter(u => u.label !== 'Unknown' && Array.isArray(u.roles) && u.roles.some(r => r === 'CRM' || r === 'ROLE_CRM'));
        setEmployees(mapped);
      } catch (err) { console.error('Failed to fetch employees:', err); }
      finally { setLoadingEmployees(false); }
    };
    fetch();
  }, []);

  /* ── Fetch contacts — only when NOT locked ── */
  useEffect(() => {
    if (lockContact) return; // skip — contact is already locked
    const fetch = async () => {
      try {
        const res  = await contactsAPI.getAllContacts();
        const data = Array.isArray(res.data) ? res.data : [];
        const mapped = data
          .map(c => ({
            value: c.id,
            label: c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Unknown',
          }))
          .filter(c => c.label !== 'Unknown');
        setContacts(mapped);
      } catch (err) { console.error('Failed to fetch contacts:', err); }
      finally { setLoadingContacts(false); }
    };
    fetch();
  }, [lockContact]);

  /* ── Fetch companies ── */
  useEffect(() => {
    const fetch = async () => {
      try {
        const res  = await companiesAPI.getAllCompanies();
        const data = Array.isArray(res.data) ? res.data : [];
        const mapped = data
          .map(c => ({ value: c.id, label: c.companyName || c.name || 'Unknown' }))
          .filter(c => c.label !== 'Unknown');
        setCompanies(mapped);
      } catch (err) { console.error('Failed to fetch companies:', err); }
      finally { setLoadingCompanies(false); }
    };
    fetch();
  }, []);

  /* ── Sync linkedAccount if prop changes (company just created) ── */
  useEffect(() => {
    if (linkedAccount) {
      setFormData(prev => ({ ...prev, linkedAccount }));
    }
  }, [linkedAccount]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = {};
    if (!formData.subject.trim()) errs.subject = 'Subject is required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setTimeout(() => {
        const el = document.querySelector('[name="subject"]');
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus?.(); }
      }, 50);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        type:          formData.type || null,
        priority:      formData.priority || null,
        reminder:      formData.reminder || 'NO_REMINDER',
        assignedTo:    formData.assignedTo || null,
        linkedContact: formData.linkedContact || null,
        linkedAccount: formData.linkedAccount || null,
        // datetime-local gives "YYYY-MM-DDTHH:mm" (naive local time); append :00 — no UTC conversion (F23)
        dueDate: formData.dueDate
          ? `${formData.dueDate}:00`
          : null,
      };
      await tasksAPI.createTask(payload);
      toast.success("Task created successfully!");
      onCreate?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create task");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white shadow-2xl w-full max-w-2xl h-full flex flex-col animate-slideInRight overflow-hidden">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Create Task</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition text-xl font-bold">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Task Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Task Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input type="text" name="subject" value={formData.subject} onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent ${errors.subject ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                    placeholder="Enter task subject" />
                  {errors.subject && <p className="text-red-600 text-xs mt-1 flex items-center gap-1">⚠ {errors.subject}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select name="type" value={formData.type} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent">
                      <option value="">Select Type</option>
                      <option value="CALL">Call</option>
                      <option value="EMAIL">Email</option>
                      {/* <option value="MEETING">Meeting</option> */}
                      {/* <option value="FOLLOW_UP">Follow Up</option> */}
                      <option value="TODO">To Do</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date &amp; Time</label>
                    <input type="datetime-local" name="dueDate" value={formData.dueDate} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reminder</label>
                    <select name="reminder" value={formData.reminder} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent">
                      <option value="NO_REMINDER">No Reminder</option>
                      <option value="THIRTY_MINUTES_BEFORE">30 Minutes Before</option>
                      <option value="ONE_HOUR_BEFORE">1 Hour Before</option>
                      <option value="ONE_DAY_BEFORE">1 Day Before</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select name="priority" value={formData.priority} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent">
                      <option value="">Select Priority</option>
                      <option value="HIGH">High</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="LOW">Low</option>
                      <option value="NONE">None</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select name="status" value={formData.status} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent">
                      <option value="PENDING">Pending</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="CANCELLED">Cancelled</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Assignment */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Assignment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                  <SearchableDropdown
                    value={formData.assignedTo}
                    onChange={val => setFormData(prev => ({ ...prev, assignedTo: val }))}
                    options={employees}
                    placeholder="Select team member..."
                    loading={loadingEmployees}
                  />
                </div>

                {/* ── Linked Contact ──
                    lockContact=true  → locked field (from ContactPreviewPage)
                    lockContact=false → free searchable dropdown (from Tasks page)
                ── */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                    Linked Contact
                    {lockContact && (
                      <span className="text-[10px] text-blue-500 font-normal bg-blue-50 border border-blue-200 rounded px-1 py-0.5">
                        Auto-linked
                      </span>
                    )}
                  </label>
                  {lockContact ? (
                    /* Locked display */
                    <>
                      <SearchableDropdown
                        value={formData.linkedContact}
                        onChange={() => {}}
                        options={linkedContactName
                          ? [{ value: linkedContact, label: linkedContactName }]
                          : []}
                        placeholder="Contact"
                        loading={false}
                        disabled={true}
                      />
                      <p className="text-[11px] text-gray-400 mt-1">
                        Automatically linked to the current contact
                      </p>
                    </>
                  ) : (
                    /* Free searchable dropdown */
                    <SearchableDropdown
                      value={formData.linkedContact}
                      onChange={val => setFormData(prev => ({ ...prev, linkedContact: val }))}
                      options={contacts}
                      placeholder="Select contact..."
                      loading={loadingContacts}
                    />
                  )}
                </div>

                {/* Linked Account — always editable; pre-filled if company was just assigned */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                    Linked Account
                    {linkedAccount && linkedAccountName && (
                      <span className="text-[10px] text-green-600 font-normal bg-green-50 border border-green-200 rounded px-1 py-0.5">
                        Pre-filled
                      </span>
                    )}
                  </label>
                  <SearchableDropdown
                    value={formData.linkedAccount}
                    onChange={val => setFormData(prev => ({ ...prev, linkedAccount: val }))}
                    options={companies}
                    placeholder="Select company..."
                    loading={loadingCompanies}
                  />
                  {linkedAccount && linkedAccountName && (
                    <p className="text-[11px] text-green-600 mt-1">
                      Pre-filled with: <strong>{linkedAccountName}</strong>
                    </p>
                  )}
                </div>

              </div>
            </div>

            {/* Details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea name="description" value={formData.description} onChange={handleChange} rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                    placeholder="Enter task description..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea name="notes" value={formData.notes} onChange={handleChange} rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                    placeholder="Additional notes..." />
                </div>
              </div>
            </div>

          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
          <button onClick={onClose}
            className="px-5 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition font-medium">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading}
            className="px-5 py-2 text-sm rounded-lg bg-[#1C4E6D] text-white hover:bg-[#153b52] transition font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? "Creating..." : "Create Task"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slideInRight { animation: slideInRight 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default CreateTaskModal;